import type { AmlPepStatus, RegulatoryExclusion } from './data/types';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../utils/constants/commonConstants';

export const DASH = '—';

export const SUB_OPTION_STANDALONE_ID = 'standalone';
export const SUB_OPTION_CONSOLIDATED_ID = 'consolidated';
export const SUB_OPTION_CONFIRMED_ID = 'confirmed';
export const SUB_OPTION_PROBABLE_ID = 'probable';
export const SUB_OPTION_CHARGE_DETAILS_ID = 'chargeDetails';
export const SUB_OPTION_DIRECTOR_DETAILS_ID = 'directorDetails';
export const SUB_OPTION_INDIVIDUAL_BANKS_ID = 'individualBanks';

export const AUDITED_VERSIONS = [
    { id: SUB_OPTION_STANDALONE_ID, label: AUDITED_TYPE_STANDALONE, hasKey: 'hasStandalone', statementsKey: 'standalone' },
    { id: SUB_OPTION_CONSOLIDATED_ID, label: AUDITED_TYPE_CONSOLIDATED, hasKey: 'hasConsolidated', statementsKey: 'consolidated' },
] as const;

const SUB_OPTION_DEFAULT_ON: Record<string, boolean> = {
    [SUB_OPTION_PROBABLE_ID]: false,
    [SUB_OPTION_DIRECTOR_DETAILS_ID]: false,
};
// Every switch not listed above, including the per-account bank switches, defaults on.
export const subOptionDefaultOn = (id: string): boolean => SUB_OPTION_DEFAULT_ON[id] ?? true;
export const subOptionOn = (options: Record<string, boolean> | undefined, id: string): boolean => options?.[id] ?? subOptionDefaultOn(id);
const BANK_ACCOUNT_SUB_OPTION_PREFIX = 'account:';
export const bankAccountSubId = (accountNumber: string) => `${BANK_ACCOUNT_SUB_OPTION_PREFIX}${accountNumber}`;
export const bankAccountSuffix = (accountNumber: string) => `(${accountNumber})`;
export const bankAccountLabel = (bankName: string, accountNumber: string) => `${bankName} ${bankAccountSuffix(accountNumber)}`;
export const INDIVIDUAL_BANK_ANALYSIS_LABEL = 'Individual Bank Analysis';
export const SELECT_BANK_ACCOUNTS_LABEL = 'Select Bank Accounts:';
export const BANKING_RAIL_TITLE = 'Banking';

export const CONFIRMED_MATCHES_LABEL = 'Confirmed Matches';
export const PROBABLE_MATCHES_LABEL = 'Probable Matches';
export const PROBABLE_BLOCK_TITLE = 'Probable matches';
export const PROBABLE_AGAINST_TITLE = 'Against the company';
export const PROBABLE_BY_TITLE = 'Filed by the company';
export const PROBABLE_EXCLUDED_NOTE = '(Probable cases are excluded from this report)';
export const CONFIRMED_EXCLUDED_NOTE = '(Confirmed matches are excluded from this report)';
export const NO_PROBABLE_CASES_NOTE = 'No probable legal cases found';
export const REGULATORY_EXCLUSIONS: Record<RegulatoryExclusion, { status: AmlPepStatus; note: string }> = {
    probable: { status: 'watch', note: PROBABLE_EXCLUDED_NOTE },
    confirmed: { status: 'hit', note: CONFIRMED_EXCLUDED_NOTE },
};
export const CHARGE_DETAILS_LABEL = 'Include Charge Details';
export const DIRECTOR_DETAILS_LABEL = 'Include Director Details';
export const MCA_SUMMARY_HOLDERS = new Set(['Total Open Charges', 'Total Closed Charges']);

export const AUDITED_MODULE_TITLE = 'Audited Financials';
export const LEGAL_MODULE_TITLE = 'Legal and Risk Checks';
export const RISK_GRADE_MODULE_TITLE = 'Risk Grade';
export const DEBT_DETAILS_MODULE_TITLE = 'Debt Details';
export const MCA_CHARGES_SUBSECTION_TITLE = 'MCA charges';
export const PROVISIONAL_SECTION_TITLE = 'Provisional Financial Statements';
export const DATA_NOT_AVAILABLE_LABEL = 'Data Not Available';
export const UNLOCK_MODULE_TOOLTIP = 'Unlock this module first to add inside report';

export const CASE_KIND_CONFIRMED = 'confirmed';
export const CASE_KIND_PROBABLE = 'probable';

export const CLASSIFICATION_COLUMN_KEY = 'classificationName';
export const PERIOD_STATUS_NOT_AVAILABLE = 'NOT_AVAILABLE';
export const YEARS_NOT_SHOWN_LABEL = 'Years not shown';
export const OUTSIDE_DISPLAYED_RANGE_LABEL = 'Outside displayed range';

export const GO_TO_SECTION_LABEL = 'Go to';
export const VERSION_BAND_SUFFIX = 'Financials';
export const STATEMENT_VERSION_SEPARATOR = ' · ';

export const REPORT_ANCHOR_ATTR = 'data-report-anchor';
export const reportAnchorId = (moduleId: string, subId: string) => `${moduleId}.${subId}`;

export const REPORT_MODULE_ID = {
    auditedStatements: 'auditedStatements',
    bankingOverview: 'bankingOverview',
    legalRisk: 'legalRisk',
    management: 'management',
    mcaCharges: 'mcaCharges',
} as const;
